import { useCallback, useState } from 'react';
import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import EventCardBadge from 'modules/event-cards/badge/index.js';
import EventCardMarketsSlider from 'modules/event-cards/markets-slider/index.js';
import { useEventItem, useEventsLoadDone } from 'modules/common/hooks';
import withEventCardSkeleton from 'modules/event-cards/skeleton/with-event-card-skeleton.js';
import CardBackground from 'modules/event-cards/background/index.js';
import GameLine from 'modules/event-cards/match/common/game-line.js';
import EventCardsTypes from 'modules/event-cards/types.js';

const { EventTimingStatus, FlexPane, Text, Button, SportAndCountryIcon } =
    MojitoPresentation.Components;
const Scoreboard = MojitoPresentation.Components.Scoreboard.default;
const { IntentActionTrigger } = MojitoPresentation.Base.Intent;
const { NavigationPathBuilder } = MojitoPresentation.Base.Navigation;
const EventsUtils = MojitoServices.SportsContent.Events.utils;
const VideoStreamsUtils = MojitoServices.VideoStreaming.utils;

const MatchEventCardAvantgarde = withEventCardSkeleton(
    props => {
        const {
            mojitoTools,
            eventId,
            marketId,
            gameLine,
            colorMode,
            shouldRequestData,
            elementRef,
        } = props;
        const { config, style, appContext, stringResolver } = mojitoTools;
        const isGameLineCard = !!gameLine;

        const [isCardHovered, setIsCardHovered] = useState(false);
        const onCardMouseEnter = useCallback(() => setIsCardHovered(true), []);
        const onCardMouseLeave = useCallback(() => setIsCardHovered(false), []);

        const event = useEventItem(eventId, shouldRequestData);
        const onEventClick = useCallback(() => {
            IntentActionTrigger.showSportEvent(event.sportId, event.id);
            appContext.analyticsEmitter.emitAnalytics('eventCardClicked', event.canonicalName);
        }, [event?.sportId, event?.id, event?.canonicalName, appContext.analyticsEmitter]);
        const mode = colorMode || config.mode;
        const eventCardDetailsConfig =
            mode === EventCardsTypes.COLOR_MODES.DARK
                ? style.eventCardDetailsDark
                : style.eventCardDetails;

        if (!event) {
            return null;
        }

        const altText =
            appContext.sportName(event.sportId) || stringResolver.resolveString('$GENERIC.DEFAULT');
        const imgUrl = config.showCustomBackgroundImage ? props.imgUrl : undefined;
        const showBackground =
            imgUrl ||
            (config.showBackgroundSportImage &&
                !config.noBackgroundImageSports.includes(event.sportId));

        return (
            <div
                style={style.cardWrapper}
                ref={elementRef}
                onMouseEnter={onCardMouseEnter}
                onMouseLeave={onCardMouseLeave}
            >
                <FlexPane
                    class="ta-MatchEventCardAvantgarde"
                    config={eventCardDetailsConfig.eventCard.container}
                >
                    {showBackground && (
                        <CardBackground
                            config={config.cardBackground}
                            sportId={event.sportId}
                            imgUrl={imgUrl}
                        />
                    )}
                    <FlexPane class="ta-badgeRow" config={config.badgeRow}>
                        <EventCardBadge config={config.categoryBadge} />
                    </FlexPane>
                    <Button
                        class="ta-clickableArea"
                        onClick={onEventClick}
                        hrefLink={NavigationPathBuilder.sportEventPath(
                            event.sportId,
                            event.id,
                            appContext.routeResolver
                        )}
                        config={eventCardDetailsConfig.eventCard.clickableArea}
                    >
                        <FlexPane class="ta-headerContainer" config={config.headerContainer}>
                            <FlexPane class="ta-headerLeft" config={config.headerLeft}>
                                <SportAndCountryIcon
                                    sportId={event.sportId}
                                    countryCode={event.countryCode}
                                    countryName={event.countryName}
                                    alt={altText}
                                    config={eventCardDetailsConfig.sportCountryIcons}
                                />
                                <Text
                                    class="ta-headerTitle"
                                    config={eventCardDetailsConfig.eventCard.title}
                                >
                                    {event.typeName}
                                </Text>
                            </FlexPane>
                            {(config.showStartTime || event.details.inPlay) && (
                                <EventTimingStatus
                                    config={eventCardDetailsConfig.headerTime}
                                    eventItem={event}
                                    hasStreaming={VideoStreamsUtils.isStreamAvailable(event)}
                                    hasClockDetails={EventsUtils.hasInPlayClockDetails(event)}
                                    inplayTime={EventsUtils.getEventInplayTime(event)}
                                />
                            )}
                        </FlexPane>
                        <Scoreboard
                            event={event}
                            config={eventCardDetailsConfig.scoreboard}
                            isOutright={EventsUtils.isOutrightEventType(event.eventType)}
                            inplayTime={EventsUtils.getEventInplayTime(event)}
                            hasClockDetails={EventsUtils.hasInPlayClockDetails(event)}
                        />
                    </Button>
                    <FlexPane
                        config={config.participantsAndMarketsContainer}
                        class={'ta-participantsAndMarketsContainer'}
                    >
                        {isGameLineCard ? (
                            <GameLine
                                {...props}
                                config={eventCardDetailsConfig.gameLineMarketContent}
                                event={event}
                                onEventClick={onEventClick}
                            />
                        ) : (
                            <EventCardMarketsSlider
                                config={config.marketsSlider}
                                event={event}
                                marketId={marketId}
                                shouldRequestData={shouldRequestData}
                                isCardHovered={isCardHovered}
                            />
                        )}
                    </FlexPane>
                </FlexPane>
            </div>
        );
    },
    props => useEventsLoadDone([props.eventId]),
    config => config.eventCardDetails.eventCard.skeletonLoader
);

MatchEventCardAvantgarde.getStyle = (config, mode, merge) => ({
    cardWrapper: {
        position: 'relative',
    },
    eventCardDetails: config.eventCardDetails,
    eventCardDetailsDark: merge(config.eventCardDetails, config.eventCardDetailsDark || {}),
});

export default MatchEventCardAvantgarde;
